import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateWeeklyTodoInput,
  UpdateWeeklyProspectInput,
  UpdateWeeklyTodoInput,
  WeeklyDetail,
  WeeklyHistory,
  WeeklyLead,
  WeeklyNoteSaved,
  WeeklyOverviewRow,
} from "@msc/shared";

import { api } from "../lib/api.js";

export const weeklyKeys = {
  all: ["weekly"] as const,
  leads: () => [...weeklyKeys.all, "leads"] as const,
  history: (employeeId: string, segmentId: string) => [...weeklyKeys.all, "history", employeeId, segmentId] as const,
  detail: (weeklyId: string) => [...weeklyKeys.all, "detail", weeklyId] as const,
  overview: () => [...weeklyKeys.all, "overview"] as const,
};

export function useWeeklyOverview() {
  return useQuery<WeeklyOverviewRow[]>({
    queryKey: weeklyKeys.overview(),
    queryFn: async () => (await api.get<{ items: WeeklyOverviewRow[] }>("/weekly/overview")).items,
  });
}

export function useWeeklyLeads() {
  return useQuery<WeeklyLead[]>({
    queryKey: weeklyKeys.leads(),
    queryFn: async () => (await api.get<{ items: WeeklyLead[] }>("/weekly/leads")).items,
  });
}

export function useWeeklyHistory(employeeId: string | undefined, segmentId: string | undefined) {
  return useQuery<WeeklyHistory>({
    queryKey: weeklyKeys.history(employeeId ?? "", segmentId ?? ""),
    enabled: Boolean(employeeId && segmentId),
    queryFn: () => api.get<WeeklyHistory>(`/weekly/leads/${employeeId}/segments/${segmentId}`),
  });
}

export function useWeekly(weeklyId: string | undefined) {
  return useQuery<WeeklyDetail>({
    queryKey: weeklyKeys.detail(weeklyId ?? ""),
    enabled: Boolean(weeklyId),
    queryFn: () => api.get<WeeklyDetail>(`/weekly/${weeklyId}`),
  });
}

/**
 * Jede Mutation, die ein ganzes Weekly zurückgibt, schreibt es direkt in den Cache.
 * `overview`: Mutation ändert Status oder Todos — dann ist auch die Übersicht veraltet.
 */
function useDetailMutation<V>(
  weeklyId: string,
  fn: (vars: V) => Promise<WeeklyDetail>,
  { overview = false }: { overview?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (detail) => {
      qc.setQueryData(weeklyKeys.detail(weeklyId), detail);
      void qc.invalidateQueries({ queryKey: [...weeklyKeys.all, "history"] });
      if (overview) void qc.invalidateQueries({ queryKey: weeklyKeys.overview() });
    },
  });
}

export function useCreateWeekly(employeeId: string, segmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<WeeklyDetail>(`/weekly/leads/${employeeId}/segments/${segmentId}`),
    onSuccess: (detail) => {
      qc.setQueryData(weeklyKeys.detail(detail.weekly.id), detail);
      void qc.invalidateQueries({ queryKey: weeklyKeys.history(employeeId, segmentId) });
      void qc.invalidateQueries({ queryKey: weeklyKeys.overview() });
    },
  });
}

/**
 * Notiz-Saves liefern nur Text + neue Version zurück. Wir patchen sie in den
 * gecachten Detailstand, sonst bringt ein Weg-und-zurück-Navigieren die alte
 * Version zurück und der nächste Save läuft in einen unechten 409.
 */
export function useSaveGeneralNotes(weeklyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { notes: string; version: number }) =>
      api.patch<WeeklyNoteSaved>(`/weekly/${weeklyId}/notes`, vars),
    onSuccess: (saved) => {
      qc.setQueryData<WeeklyDetail>(weeklyKeys.detail(weeklyId), (alt) =>
        alt
          ? { ...alt, weekly: { ...alt.weekly, generalNotes: saved.notes, generalNotesVersion: saved.version } }
          : alt,
      );
    },
  });
}

export function useSaveRowNote(weeklyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { customerId: string | null; prospectId: string | null; notes: string; version: number }) => {
      const pfad = vars.customerId
        ? `/weekly/${weeklyId}/rows/customer/${vars.customerId}/note`
        : `/weekly/${weeklyId}/rows/prospect/${vars.prospectId}/note`;
      return api.put<WeeklyNoteSaved>(pfad, { notes: vars.notes, version: vars.version });
    },
    onSuccess: (saved, vars) => {
      qc.setQueryData<WeeklyDetail>(weeklyKeys.detail(weeklyId), (alt) =>
        alt
          ? {
              ...alt,
              rows: alt.rows.map((r) =>
                (vars.customerId ? r.customerId === vars.customerId : r.prospectId === vars.prospectId)
                  ? { ...r, notes: saved.notes, notesVersion: saved.version }
                  : r,
              ),
            }
          : alt,
      );
    },
  });
}

export function useCreateTodo(weeklyId: string) {
  return useDetailMutation(weeklyId, (input: CreateWeeklyTodoInput) =>
    api.post<WeeklyDetail>(`/weekly/${weeklyId}/todos`, input),
    { overview: true },
  );
}

export function useUpdateTodo(weeklyId: string) {
  return useDetailMutation(weeklyId, ({ todoId, ...input }: UpdateWeeklyTodoInput & { todoId: string }) =>
    api.patch<WeeklyDetail>(`/weekly/${weeklyId}/todos/${todoId}`, input),
    { overview: true },
  );
}

export function useCreateProspect(weeklyId: string) {
  return useDetailMutation(weeklyId, (name: string) =>
    api.post<WeeklyDetail>(`/weekly/${weeklyId}/prospects`, { name }),
  );
}

export function useUpdateProspect(weeklyId: string) {
  return useDetailMutation(weeklyId, ({ prospectId, ...input }: UpdateWeeklyProspectInput & { prospectId: string }) =>
    api.patch<WeeklyDetail>(`/weekly/${weeklyId}/prospects/${prospectId}`, input),
  );
}

export function useCloseWeekly(weeklyId: string) {
  return useDetailMutation(weeklyId, () => api.post<WeeklyDetail>(`/weekly/${weeklyId}/close`), { overview: true });
}

export function useReopenWeekly(weeklyId: string) {
  return useDetailMutation(weeklyId, () => api.post<WeeklyDetail>(`/weekly/${weeklyId}/reopen`), {
    overview: true,
  });
}
